// Real-time layer for the student pass workflow. Same Socket.io connection
// to passes-api as useSchoolPasses; the difference is scope — a student's
// socket is auto-joined to `user:{userId}` only, never the school room. The
// connect/reconnect/resync/terminal-status-guard mechanics live in the
// shared usePassSocket base hook — see that file for the full contract
// (no event replay on reconnect, resync-on-connect, terminal-state guard).
//
// recentPasses merges the one-time history fetch (listPasses({limit: 10}))
// with the live passesById state so a student's own pass activity during
// the session shows up immediately instead of only reflecting the fetch
// taken at mount time.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePassSocket, byRequestedAtDesc, type Pass } from './usePassSocket';
import { listPasses } from '../api/studentPassesApi';
import { logError } from '../utils/errorHandling';

// The pass element type is usePassSocket's `Pass` — the same backend entity
// the REST history fetch returns (listPasses' PassResponse assigns into it).
// Using the one type for both sources keeps the merge sound; the generated
// PassResponse.status enum already covers every live socket state (WAITING/
// COMPLETED included) since PR #362, so no status widening is needed here.

// Reuse usePassSocket's own return shape rather than redefining its pass,
// connection-status, and last-event types here.
type UsePassSocketResult = ReturnType<typeof usePassSocket>;

// Events delivered to a student's own `user:{id}` room. 'pass:requested' is
// intentionally excluded — it's staff-only (the requesting student already
// has the pass from the REST create response).
const PASS_EVENT_NAMES: string[] = [
  'pass:approved',
  'pass:waiting',
  'pass:denied',
  'pass:returned',
  'pass:cancelled',
  'pass:expired',
];

/**
 * Real-time + REST-backed view of the current student's passes.
 */
export interface UseStudentPassEventsResult {
  passes: UsePassSocketResult['passes'];
  recentPasses: Pass[];
  connectionStatus: UsePassSocketResult['connectionStatus'];
  lastEvent: UsePassSocketResult['lastEvent'];
  refresh: () => Promise<void>;
}

export function useStudentPassEvents(): UseStudentPassEventsResult {
  const { passes, passesById, connectionStatus, lastEvent, resync } =
    usePassSocket(PASS_EVENT_NAMES, 'useStudentPassEvents');
  const [recentPassesHistory, setRecentPassesHistory] = useState<Pass[]>([]);

  // One-time history fetch for the "Recent Passes" UI.
  useEffect(() => {
    let cancelled = false;
    listPasses({ limit: 10 })
      .then(({ data }) => {
        if (!cancelled) setRecentPassesHistory(data);
      })
      .catch((error: unknown) => {
        logError(error, 'client_socket', 'useStudentPassEvents:history');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const recentPasses = useMemo<Pass[]>(() => {
    const merged = new Map<Pass['id'], Pass>(
      recentPassesHistory.map((p): [Pass['id'], Pass] => [p.id, p])
    );
    Object.values(passesById).forEach((p) => merged.set(p.id, p));
    return [...merged.values()].sort(byRequestedAtDesc).slice(0, 10);
  }, [recentPassesHistory, passesById]);

  // Force a REST resync outside the 'connect' event — used by the
  // dashboard as a race-safety valve right after local mutations (e.g.
  // immediately after createPass(), before any socket event confirms it).
  const refresh = useCallback<() => Promise<void>>(
    () => resync(),
    [resync]
  );

  return { passes, recentPasses, connectionStatus, lastEvent, refresh };
}

export default useStudentPassEvents;
